import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css } from "styled-components/native";
import ValueFilterText from "@components/atoms/Filters/ValueFilter";
import { Platform, Pressable } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { SelectionType } from "@components/atoms/Filters/types";
import ShowMoreButton from "@components/atoms/Filters/ShowMoreButton";
import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import SelectedItemsTitle from "@components/atoms/Filters/SelectedItemsTitle";
import {
  removeFiltersPayload,
  setFiltersPayload,
  setStartRangeIndex,
  setExcludePayload,
  resetExcludePayloadByFilterType,
  useSearchFiltersPayload,
  useSearchFiltersSlice,
  useValueFromPayloadRangeOrSingleValue,
} from "@redux/slices/searchFilters";
import { useDispatch, useSelector } from "react-redux";
import {
  cpsSectionNames,
  cpsSections,
  cpsValues,
  sectionTitleList,
  TextBoxSectionProps,
} from "@components/atoms/Filters/TextListSection/types";
import useResetFilter from "../../../../hooks/useResetFilter";
import LineViewContainer from "@scenes/requests/utils/LineView";
import useRemoveFancyColorFilters from "../FilterHooks/UseRemoveFancyColorFilters";
import { ProductCategory, screenWidth } from "@utils/constants";
import { useTheme } from "styled-components/native";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";

const TextListSection = ({
  options = [],
  selectionType = "",
  displayedRows = 2,
  sectionTitle = "",
  payloadName = [],
  sectionName = "",
  selectedStyle,
  isSubOption = false,
  hasSeparator = false,
  translation,
  subSectionName = "",
  minWidth,
  expandView,
  fromUnifiedFilters = false,
  selectedProduct = ProductCategory.DIAMOND,
  columns = 2,
  justifyContent = "flex-start",
  isCollapsible = false,
  dependencies = [],
}: TextBoxSectionProps) => {
  if (!columns) {
    return;
  }
  const theme = useTheme();
  minWidth =
    minWidth ||
    (theme.isLargeScreen && Platform.OS === "web" && !fromUnifiedFilters
      ? 86
      : 72);

  const dispatch = useDispatch();
  const { auxPayload: filtersPayload } = useSearchFiltersSlice();
  const apiPayload = useSearchFiltersPayload(selectedProduct);
  const { containerWidth } = useContext(FilterScreenContext);
  const isTouched = useRef(false);
  const typography = useTypographyStore();
  const startRangeIndex = useSelector(
    (state: any) => state.searchFilters?.startRangeIndex
  );
  const [showAll, setShowAll] = useState<boolean>(!displayedRows);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const reverseSelectedItemTitle = subSectionName
    ? subSectionName === "Intensity"
    : false;

  useEffect(() => {
    if (
      dependencies.length &&
      apiPayload?.[sectionName]?.[dependencies[0].key] !==
        filtersPayload?.[sectionName]?.[dependencies[0].key]
    ) {
      if (
        filtersPayload?.[sectionName]?.[dependencies[0].key]?.includes(
          dependencies[0].value
        )
      ) {
        dispatch(
          removeFiltersPayload({ sectionName, payloadName: [payloadName?.[0]] })
        );
      }
    }
  }, [dependencies]);

  const value = useValueFromPayloadRangeOrSingleValue(
    sectionName,
    payloadName,
    selectionType === SelectionType.range
  );

  const previouslySelected = useMemo(() => {
    let selected = options?.filter((option) =>
      value?.includes(option?.search_payload?.[0])
    );

    if (selectionType === SelectionType.range) {
      const rangeMin = options?.findIndex((obj) => obj?.name === value?.[0]);
      const rangeMax = options?.findIndex((obj) => obj?.name === value?.[1]);
      if (rangeMin >= 0 && rangeMax >= 0) {
        selected = options.slice(rangeMin, rangeMax + 1);
      }
    }
    return selected || [];
  }, [options, value, selectionType]);

  const [selectedItems, setSelectedItems] = useState<any[]>(
    previouslySelected || []
  );

  const isCpsSection = cpsSections.includes(subSectionName as cpsSectionNames);
  const isCpsParent = subSectionName === sectionTitleList.cpsTitle;
  const basePayloadKey = Array.isArray(payloadName)
    ? payloadName?.[0]
    : payloadName;
  const baseSectionKey =
    basePayloadKey ||
    subSectionName ||
    sectionTitle ||
    sectionName ||
    "options";
  const safeKey = (mValue: unknown) =>
    String(mValue ?? "item")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "");
  const baseTestId = `filters.${
    sectionName?.toLowerCase()?.replaceAll(" ", "-")?.toString() || "section"
  }.${safeKey(baseSectionKey)}.textList`;

  useResetFilter(() => {
    dispatch(setStartRangeIndex({ sectionName: payloadName?.[0], data: null }));
    setSelectedItems([]);
  });

  const itemsPerRow = Math.floor(containerWidth / (minWidth + SpaceType.Extra));

  const columnsWidth = useMemo(() => {
    if (theme.isMobile) {
      return (
        (screenWidth -
          (2 * SpaceType.Double +
            2 * SpaceType.Double +
            (columns - 1) * SpaceType.Extra) -
          2) /
        columns
      );
    }
    if (!itemsPerRow) return minWidth;
    const columnWidth = Math.floor(
      (containerWidth - (itemsPerRow - 1) * SpaceType.Extra) / itemsPerRow
    );
    return columnWidth;
  }, [itemsPerRow, containerWidth]);

  const actualOptions = useMemo(() => {
    if (theme.isMobile) {
      const numberOfItems = options.length;
      const numberOfPlaceholders =
        numberOfItems % columns === 0 ? 0 : columns - (numberOfItems % columns);
      return [...options, ...Array(numberOfPlaceholders).fill({})];
    } else {
      if (itemsPerRow === 0) return [];
      const numberOfItems = options.length;
      const numberOfPlaceholders =
        numberOfItems % itemsPerRow === 0
          ? 0
          : itemsPerRow - (numberOfItems % itemsPerRow);
      if (fromUnifiedFilters) {
        return [...options];
      } else {
        return [...options, ...Array(numberOfPlaceholders).fill({})];
      }
    }
  }, [options, itemsPerRow]);

  const rows = Math.min(
    displayedRows ?? 0,
    Math.ceil(actualOptions.length / itemsPerRow)
  );

  const is3X = useCallback(() => {
    return (
      filtersPayload?.[sectionName]?.cut_from === cpsValues.excellent &&
      filtersPayload?.[sectionName]?.polish_from === cpsValues.excellent &&
      filtersPayload?.[sectionName]?.symmetry_from === cpsValues.excellent &&
      filtersPayload?.[sectionName]?.cut_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.polish_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.symmetry_to ===
        actualOptions?.[0]?.search_payload?.[0]
    );
  }, [filtersPayload, sectionName]);

  // const is3XPlus = useCallback(() => {
  //   return (
  //     filtersPayload?.[sectionName]?.cut_from === cpsValues.excellent &&
  //     filtersPayload?.[sectionName]?.polish_from === cpsValues.excellent &&
  //     filtersPayload?.[sectionName]?.symmetry_from === cpsValues.excellent &&
  //     filtersPayload?.[sectionName]?.cut_to ===
  //       actualOptions?.[0]?.search_payload?.[0] &&
  //     filtersPayload?.[sectionName]?.polish_to ===
  //       actualOptions?.[0]?.search_payload?.[0] &&
  //     filtersPayload?.[sectionName]?.symmetry_to ===
  //       actualOptions?.[0]?.search_payload?.[0]
  //   );
  // }, [filtersPayload, sectionName]);

  const is3Xvgplus = useCallback(() => {
    return (
      filtersPayload?.[sectionName]?.cut_from === cpsValues.very_good &&
      filtersPayload?.[sectionName]?.polish_from === cpsValues.very_good &&
      filtersPayload?.[sectionName]?.symmetry_from === cpsValues.very_good &&
      filtersPayload?.[sectionName]?.cut_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.polish_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.symmetry_to ===
        actualOptions?.[0]?.search_payload?.[0]
    );
  }, [filtersPayload, sectionName]);

  const is8X = useCallback(() => {
    return (
      filtersPayload?.[sectionName]?.cut_from === cpsValues.eight_x &&
      filtersPayload?.[sectionName]?.polish_from === cpsValues.eight_x &&
      filtersPayload?.[sectionName]?.symmetry_from === cpsValues.eight_x &&
      filtersPayload?.[sectionName]?.cut_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.polish_to ===
        actualOptions?.[0]?.search_payload?.[0] &&
      filtersPayload?.[sectionName]?.symmetry_to ===
        actualOptions?.[0]?.search_payload?.[0]
    );
  }, [filtersPayload, sectionName]);

  useRemoveFancyColorFilters(sectionName);

  useEffect(() => {
    if (
      payloadName?.length &&
      filtersPayload?.[sectionName]?.[payloadName[0]] === undefined
    ) {
      setSelectedItems([]);
    }
  }, [filtersPayload?.[sectionName]?.[payloadName]]);

  useEffect(() => {
    if (filtersPayload?.[sectionName] === undefined) {
      setSelectedItems([]);
    }
  }, [filtersPayload?.[sectionName]]);

  useEffect(() => {
    if (
      (subSectionName === cpsSectionNames.cut ||
        subSectionName === cpsSectionNames.symmetry ||
        subSectionName === cpsSectionNames.polish) &&
      filtersPayload
    ) {
      if (filtersPayload?.[sectionName]?.hasOwnProperty(cpsValues.cps)) {
        const valueRange = filtersPayload?.[sectionName]?.[cpsValues.cps];

        const rangeMin = options?.findIndex(
          (obj) => obj?.name === valueRange?.[0]
        );

        const maxValueToSearch =
          valueRange.length === 1 ? valueRange[0] : valueRange[1];
        const rangeMax = options?.findIndex(
          (obj) => obj?.name === maxValueToSearch
        );
        setSelectedItems(options.slice(rangeMin, rangeMax + 1));
      } else {
        if (is3X() || is3Xvgplus() || is8X()) {
          setSelectedItems([]);
        }
      }
    }
  }, [
    filtersPayload?.[sectionName]?.three_x,
    filtersPayload?.[sectionName]?.three_vg_plus,
    filtersPayload?.[sectionName]?.eight_x,
  ]);

  useEffect(() => {
    if (
      subSectionName === "Lab" &&
      filtersPayload &&
      filtersPayload?.[sectionName]?.labs
    ) {
      const labFilters = filtersPayload?.[sectionName]?.labs;
      if (labFilters && labFilters.length) {
        let selected = options?.filter((option) =>
          labFilters?.includes(option?.search_payload?.[0])
        );
        setSelectedItems(selected || []);
      }
    }
  }, [filtersPayload?.[sectionName]?.labs?.length, subSectionName, options]);

  const isCpsCategory = useMemo(() => {
    const cpsValue = selectedItems.map((item) => {
      if (item?.payload_name?.[0] === cpsValues.three_x) {
        return cpsValues.three_x;
      }
      if (item?.payload_name?.[0] === cpsValues.three_vg_plus) {
        return cpsValues.three_vg_plus;
      }
      if (item?.payload_name?.[0] === cpsValues.eight_x_option) {
        return cpsValues.eight_x_option;
      }
    });
    if (cpsValue?.length === 1) {
      return cpsValue[0];
    }
    return null;
  }, [selectedItems]);

  useEffect(() => {
    if (filtersPayload?.[sectionName] === undefined) {
      setSelectedItems([]);
    }
  }, [filtersPayload?.[sectionName]]);

  useEffect(() => {
    if (!isCpsSection && !isTouched.current) {
      return;
    }

    const filterKey = payloadName?.[0];

    if (selectedItems.length === 0 && sectionName && payloadName) {
      dispatch(removeFiltersPayload({ sectionName, payloadName }));
      if (filterKey && sectionName) {
        dispatch(resetExcludePayloadByFilterType({ sectionName, filterKey }));
      }
      setTimeout(() => {
        if (
          filtersPayload?.[sectionName]?.three_x ||
          filtersPayload?.[sectionName]?.three_vg_plus ||
          filtersPayload?.[sectionName]?.eight_x
        ) {
          dispatch(
            removeFiltersPayload({
              sectionName,
              payloadName: [
                cpsValues.three_x,
                cpsValues.three_vg_plus,
                cpsValues.eight_x_option,
              ],
            })
          );
        }
      }, 200);
    } else {
      const payload: any = {};
      if (selectionType === SelectionType.range) {
        payload[payloadName[0]] = selectedItems[0]?.search_payload?.[0];
        payload[payloadName[1]] =
          selectedItems?.[selectedItems.length - 1]?.search_payload?.[0];
      } else {
        if (selectionType === SelectionType.single && isCpsCategory) {
          const cpsPayload = isCpsCategory;
          if (cpsPayload) {
            payload[cpsPayload] = true;
            dispatch(setFiltersPayload({ sectionName, data: payload }));
          }
          if (
            filtersPayload?.[sectionName]?.three_x &&
            !selectedItems.some((obj) => obj?.name === cpsValues.three_x)
          ) {
            dispatch(
              removeFiltersPayload({
                sectionName,
                payloadName: [cpsValues.three_x],
              })
            );
          }
          if (
            filtersPayload?.[sectionName]?.eight_x &&
            !selectedItems.some((obj) => obj?.name === cpsValues.eight_x)
          ) {
            dispatch(
              removeFiltersPayload({
                sectionName,
                payloadName: [cpsValues.eight_x_option],
              })
            );
          }
          if (
            filtersPayload?.[sectionName]?.three_vg_plus &&
            !selectedItems.some((obj) => obj?.name === cpsValues.three_vg_plus)
          ) {
            dispatch(
              removeFiltersPayload({
                sectionName,
                payloadName: [cpsValues.three_vg_plus],
              })
            );
          }
        }
        payloadName?.forEach((name) => {
          payload[name] = selectedItems.flatMap(
            (item) => item?.search_payload ?? []
          );
        });
      }

      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }

      if (filterKey && sectionName) {
        const excludeValues = selectedItems
          .filter((item) => item?.exclude_search_payload?.length > 0)
          .flatMap((item) => item?.exclude_search_payload ?? []);

        if (excludeValues.length > 0) {
          dispatch(
            setExcludePayload({ sectionName, filterKey, values: excludeValues })
          );
        } else {
          dispatch(resetExcludePayloadByFilterType({ sectionName, filterKey }));
        }
      }
    }
  }, [selectedItems]);

  useEffect(() => {
    if (selectedItems.length > 0) {
      setShowAll(
        selectedItems.some(
          (item) => actualOptions.indexOf(item) + 1 > rows * itemsPerRow
        )
      );
    }
  }, []);

  useEffect(() => {
    if (isCpsSection) {
      updateThreeXStates();
    }
  }, [
    filtersPayload?.[sectionName]?.cut_from,
    filtersPayload?.[sectionName]?.polish_from,
    filtersPayload?.[sectionName]?.symmetry_from,
    filtersPayload?.[sectionName]?.cut_to,
    filtersPayload?.[sectionName]?.polish_to,
    filtersPayload?.[sectionName]?.symmetry_to,
  ]);

  const renderLabel = (label?: string) => {
    if (label?.startsWith("{")) {
      label = label.replaceAll("{", "").replaceAll("}", "");
    }
    return !label ? "" : translation(label);
  };

  const updateThreeXStates = useCallback(() => {
    if (is3X()) {
      const mOption = options?.find(
        (Option) =>
          Option?.name === sectionTitleList.three_x_title_plus ||
          Option?.name === sectionTitleList.three_x_title
      );
      if (mOption) {
        dispatch(
          setStartRangeIndex({ sectionName: payloadName?.[0], data: 0 })
        );
        setSelectedItems([mOption]);
      }
    } else if (is3Xvgplus()) {
      const mOption = options?.find(
        (Option) => Option?.name === sectionTitleList.three_vg_plus_title
      );
      if (mOption) {
        dispatch(
          setStartRangeIndex({ sectionName: payloadName?.[0], data: 0 })
        );
        setSelectedItems([mOption]);
      }
    } else if (is8X()) {
      const mOption = options?.find(
        (Option) => Option?.name === sectionTitleList.eight_x_title
      );
      if (mOption) {
        dispatch(
          setStartRangeIndex({ sectionName: payloadName?.[0], data: 0 })
        );
        setSelectedItems([mOption]);
      }
    } else {
      dispatch(
        removeFiltersPayload({
          sectionName,
          payloadName: [
            cpsValues.three_x,
            cpsValues.three_vg_plus,
            cpsValues.cps,
          ],
        })
      );
    }
  }, [is3X, is3Xvgplus, is8X, options]);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      isTouched.current = true;

      if (selectionType === SelectionType.single) {
        dispatch(
          setStartRangeIndex({ sectionName: payloadName?.[0], data: null })
        );
        if (selectedItems.includes(item)) {
          setSelectedItems([]);
        } else {
          if (isCpsParent && expandView) {
            expandView();
          }
          setSelectedItems([item]);
        }
      } else if (selectionType === SelectionType.multiple) {
        setSelectedItems((prevSelected) =>
          prevSelected.includes(item)
            ? prevSelected.filter((prevItem) => prevItem !== item)
            : [...prevSelected, item]
        );
      } else if (selectionType === SelectionType.range) {
        if (selectedItems.length === 0) {
          dispatch(
            setStartRangeIndex({ sectionName: payloadName?.[0], data: index })
          );
        }
        if (startRangeIndex?.[payloadName?.[0]] === null) {
          dispatch(
            setStartRangeIndex({ sectionName: payloadName?.[0], data: index })
          );
          setSelectedItems([item]);
        } else {
          const minIndex = Math.min(startRangeIndex?.[payloadName?.[0]], index);
          const maxIndex = Math.max(startRangeIndex?.[payloadName?.[0]], index);
          const range = options.slice(minIndex, maxIndex + 1);
          const currentMin = options.indexOf(selectedItems[0]);
          const currentMax = options.indexOf(
            selectedItems[selectedItems.length - 1]
          );
          if (selectedItems.length === 1 && currentMax !== index) {
            setSelectedItems(range);
          } else {
            if (index === currentMin - 1) {
              setSelectedItems([item, ...selectedItems]);
            } else if (index === currentMax + 1) {
              setSelectedItems([...selectedItems, item]);
            } else if (index === currentMin) {
              dispatch(
                setStartRangeIndex({
                  sectionName: payloadName?.[0],
                  data: index + 1,
                })
              );
              setSelectedItems(selectedItems.slice(1));
            } else if (index === currentMax) {
              dispatch(
                setStartRangeIndex({
                  sectionName: payloadName?.[0],
                  data: index - 1,
                })
              );
              setSelectedItems(selectedItems.slice(0, -1));
            } else {
              dispatch(
                setStartRangeIndex({
                  sectionName: payloadName?.[0],
                  data: index,
                })
              );
              setSelectedItems([item]);
            }
          }
        }
      }
    },
    [selectionType, startRangeIndex?.[payloadName?.[0]], options, selectedItems]
  );

  if (!actualOptions || !actualOptions.length) return <></>;

  return (
    <>
      <ParentContainer>
        {!isSubOption && itemsPerRow > 0 && (
          <>
            {!isCollapsible && (
              <TitleSection>
                <SectionTitleWrap>
                  <VDBText
                    isBold
                    type={typography["H3-M"]}
                    color={theme["core-05"]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {sectionTitle}
                  </VDBText>
                </SectionTitleWrap>
                {selectedItems.length > 0 && (
                  <SelectedItemsTitle
                    selectionType={selectionType}
                    selectedItems={selectedItems}
                    reverseSelectedItemTitle={reverseSelectedItemTitle}
                  />
                )}
              </TitleSection>
            )}
            {(sectionTitle || selectedItems.length > 0) && (
              <Spacer size={SpaceType.Extra} />
            )}
          </>
        )}
        {itemsPerRow > 0 && (
          <Container justifyContent={justifyContent}>
            {actualOptions
              .slice(
                0,
                showAll
                  ? actualOptions.length
                  : theme.isMobile
                  ? displayedRows * columns
                  : rows * itemsPerRow
              )
              .map((item, index) => {
                const optionKey = safeKey(
                  item?.payload_name?.[0] ||
                    item?.search_payload?.[0] ||
                    item?.name ||
                    item?.label ||
                    index
                );

                const optionTestId = `${baseTestId}.option.${optionKey}`;

                return (
                  <IgnoredContainer
                    key={index}
                    dataSet={{
                      ddFrustrationIgnore: "true",
                      ddRageClickIgnore: "true",
                      ddDoubleClickIgnore: "true",
                    }}
                    className="dd-rage-click-ignore dd-double-click-ignore"
                    data-dd-rage-click-ignore="true"
                    data-dd-double-click-ignore="true"
                  >
                    <Pressable
                      onHoverIn={() => setHoveredIndex(index)}
                      onHoverOut={() => setHoveredIndex(null)}
                      onPress={() => {
                        if (item.name) handleItemSelection(item, index);
                      }}
                      disabled={!item.name}
                      testID={optionTestId}
                      data-testId={optionTestId}
                    >
                      <FilterButton
                        isSelected={selectedItems.includes(item)}
                        value={renderLabel(item.label)}
                        width={columnsWidth}
                        selectedStyle={selectedStyle}
                        isHovered={index === hoveredIndex}
                        applyMinWidth={false}
                      />
                    </Pressable>
                  </IgnoredContainer>
                );
              })}
          </Container>
        )}
        {!!rows && options.length > rows * itemsPerRow && (
          <ShowMoreContainer
            fromUnifiedFilters={fromUnifiedFilters}
            isSubOption={isSubOption}
          >
            <ShowMoreButton
              options={options}
              columns={itemsPerRow}
              showAll={showAll}
              onPressFunction={setShowAll}
              displayedRows={rows}
              selectedProduct={selectedProduct}
            />
          </ShowMoreContainer>
        )}
      </ParentContainer>
      {hasSeparator && (
        <>
          <LineViewContainer background="core-03" />
          <Spacer size={SpaceType.Extra} />
        </>
      )}
    </>
  );
};

const Container = styled.View<{ justifyContent: string }>`
  gap: ${SpaceType.Extra}px;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ justifyContent }: { justifyContent: string }) =>
    justifyContent ?? "flex-start"};
`;

const FilterButton = styled(ValueFilterText)<{
  width: number;
}>`
  width: ${({ width }: { width: number }) => width}px;
`;

const TitleSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0px;
  padding-top: ${SpaceType.Basic}px;
`;

const SectionTitleWrap = styled.View`
  flex: 1;
  min-width: 0px;
  max-width: 42%;
  ${Platform.OS === "web"
    ? css`
        overflow: hidden;
      `
    : css``}
`;

const ParentContainer = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.ExtraSmall}px;
  padding-top: ${SpaceType.ExtraSmall}px;
`;

const IgnoredContainer = styled.View``;

const ShowMoreContainer = styled.View<{
  fromUnifiedFilters: boolean;
  isSubOption: boolean;
}>`
  margin-bottom: ${({
    fromUnifiedFilters,
    isSubOption,
  }: {
    fromUnifiedFilters: boolean;
    isSubOption: boolean;
  }) => (fromUnifiedFilters && !isSubOption ? -SpaceType.Basic : 0)}px;
`;

export default memo(TextListSection);
